import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { UseDimensionConfigPayload } from 'libs/end-to-end/src/use-dimension-config-payload';
import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { determineFilterStoreScope } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getPrimaryFilterFullKey } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedEmployeeGroup } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import React, { useEffect, useMemo, useState } from 'react';
import { FilterMenuDualPanel } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuDualPanel';
import { FilterMenuDualPanelItemProps } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuDualPanel';
import { FilterListBoxItem } from 'apps/sigma2-ui/src/filters-v2/components/FilterListBox';
import { SearchableFilterListBox } from 'apps/sigma2-ui/src/filters-v2/components/SearchableFilterListBox';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export const EmployeeGroupMenu = ({
  allDimensions,
  getAllLevelsForDimension,
  store,
  feature,
  focusArea,
  analysisName,
  databaseRevisionId,
  databaseId,
  organizationId,
  closeMenu,
  setPrimaryFilters,
}: {
  allDimensions: DimensionDefinition[];
  getAllLevelsForDimension: UseDimensionConfigPayload['getAllLevelsForDimension'];
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  closeMenu: () => void;
  setPrimaryFilters: (params: {
    selections: PrimaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
}) => {
  const language = useLanguage();
  const [selectedDimension, setSelectedDimension] = useState<string>('');
  const currentDimensionLevel = useMemo(
    () =>
      getSelectedEmployeeGroup({
        focusArea,
        analysisName,
        store,
        feature,
        databaseId,
        databaseRevisionId,
        organizationId,
      }),
    [
      databaseId,
      databaseRevisionId,
      feature,
      focusArea,
      analysisName,
      organizationId,
      store,
    ],
  );

  // Select the first dimension by default
  useEffect(() => {
    if (selectedDimension === '' && currentDimensionLevel !== null) {
      setSelectedDimension(currentDimensionLevel.dimension);
    }
  }, [allDimensions, currentDimensionLevel, selectedDimension]);

  const items = useMemo(() => {
    const fullKey = getPrimaryFilterFullKey({
      primaryFilterKey: 'employeeGroup',
    });
    const scope = determineFilterStoreScope({
      feature,
      fullKey,
      databaseId,
      databaseRevisionId,
      organizationId,
      focusArea,
      analysisName,
    });

    if (!scope) {
      return [];
    }

    const allItems: FilterMenuDualPanelItemProps[] = [];

    for (const dimension of allDimensions) {
      const levels = getAllLevelsForDimension(dimension.columnHeaderValue);

      const dimensionItems: FilterListBoxItem[] = levels
        .filter((level) => !level.isPrivileged)
        .map((level) => ({
          value: level.columnValue,
          translatedContent: language.levelDisplayName({
            definition: level,
          }),
          selected:
            currentDimensionLevel?.dimension === dimension.columnHeaderValue &&
            currentDimensionLevel?.level === level.columnValue,
        }));

      allItems.push({
        key: dimension.columnHeaderValue,
        translatedContent: language.dimensionDisplayName({
          definition: dimension,
        }),
        onClick: () => {
          setSelectedDimension(dimension.columnHeaderValue);
        },
        content: (
          <SearchableFilterListBox
            items={dimensionItems}
            multiple={false}
            onChange={(newItems) => {
              if (newItems.length <= 0) {
                return;
              }

              const privLevel = levels.find((level) => level.isPrivileged);

              setPrimaryFilters({
                selections: [
                  {
                    primaryFilterKey: 'employeeGroup',
                    value: [dimension.columnHeaderValue, newItems[0]],
                  },
                  ...(privLevel
                    ? [
                        {
                          primaryFilterKey: 'comparisonGroup' as const,
                          value: privLevel.columnValue,
                        },
                      ]
                    : []),
                ],
              });

              closeMenu();
            }}
          />
        ),
        selected: selectedDimension === dimension.columnHeaderValue,
        itemValue: dimension.columnHeaderValue,
      });
    }
    return allItems;
  }, [
    allDimensions,
    closeMenu,
    currentDimensionLevel?.dimension,
    currentDimensionLevel?.level,
    databaseId,
    databaseRevisionId,
    feature,
    focusArea,
    analysisName,
    getAllLevelsForDimension,
    language,
    organizationId,
    selectedDimension,
    setPrimaryFilters,
  ]);
  return <FilterMenuDualPanel items={items} bottomProps={'none'} />;
};
